import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { updateMetaTags, metaTags } from './utils/metaTags';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import TreeCalculator from './components/TreeCalculator';
import VisitorBoost from './components/VisitorBoost';
import BigBrandsZeroCarbon from './components/BigBrandsZeroCarbon';
import About from './components/About';
import Services from './components/Services';
import CertificateVerification from './components/CertificateVerification';
import WhyUs from './components/WhyUs';
import CertificateForm from './components/CertificateForm';
import Footer from './components/Footer';
import VideoSection from './components/VideoSection';
import LiveChatWidget from './components/LiveChatWidget';
import AccessibilityButton from './components/AccessibilityButton';
import CasinoTaxAdvantage from './components/CasinoTaxAdvantage';
import CasinoCarbonAdvantage from './components/CasinoCarbonAdvantage';
import CO2NeutralWeb from './components/services/CO2NeutralWeb';
import CO2Calculator from './components/services/CO2Calculator';
import CO2Certificate from './components/services/CO2Certificate';
import CO2Exchange from './components/services/CO2Exchange';
import AddCO2Badge from './components/AddCO2Badge';
import CertificateVerificationPage from './components/CertificateVerificationPage';
import CertificatePage from './components/CertificatePage';
import ContactPage from './components/ContactPage';
import CheckoutPage from './components/CheckoutPage';
import AdminPanel from './components/admin/AdminPanel';
import Datenschutzerklaerung from './pages/Datenschutzerklaerung';
import Impressum from './pages/Impressum';
import Widerrufsbelehrung from './pages/Widerrufsbelehrung';
import AGB from './pages/AGB';

// Language-aware component wrapper
const getBestLanguage = () => {
  const supported = ['tr', 'de', 'en'];
  
  // First check localStorage for saved preference
  if (typeof window !== 'undefined') {
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang && supported.includes(savedLang)) {
      return savedLang;
    }
    
    // Then detect from browser
    if (typeof navigator !== 'undefined') {
      const navLangs = navigator.languages || [navigator.language || navigator.userLanguage];
      for (let lang of navLangs) {
        lang = lang.toLowerCase();
        if (lang.startsWith('tr')) return 'tr';
        if (lang.startsWith('de')) return 'de';
        if (lang.startsWith('en')) return 'en';
      }
    }
  }
  
  // Default to German if no match (since the site is primarily for German market)
  return 'de';
};

const LanguageAwareApp = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Get language from URL path
    const path = window.location.pathname;
    const langMatch = path.match(/^\/(tr|de|en)/);
    
    if (langMatch) {
      const langFromUrl = langMatch[1];
      if (langFromUrl !== currentLanguage) {
        changeLanguage(langFromUrl);
      }
    } else {
      // Detect browser language if no language in URL
      const bestLang = getBestLanguage();
      if (bestLang !== currentLanguage) {
        changeLanguage(bestLang);
      }
    }
  }, [currentLanguage, changeLanguage]);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Hide Navbar and Footer on admin panel
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/tr/admin') || location.pathname.startsWith('/de/admin') || location.pathname.startsWith('/en/admin') || location.pathname.startsWith('/ufukerma123456789');

  return (
    <div className="App">
      {!isAdminRoute && <Navbar />}
      <Routes>
        {/* Language-specific routes */}
        <Route path="/tr" element={<HomePage />} />
        <Route path="/de" element={<HomePage />} />
        <Route path="/en" element={<HomePage />} />
        
        {/* Service routes with language */}
        <Route path="/tr/services/co2-neutral-web" element={<CO2NeutralWeb />} />
        <Route path="/de/services/co2-neutral-web" element={<CO2NeutralWeb />} />
        <Route path="/en/services/co2-neutral-web" element={<CO2NeutralWeb />} />
        
        <Route path="/tr/services/co2-calculator" element={<CO2Calculator />} />
        <Route path="/de/services/co2-calculator" element={<CO2Calculator />} />
        <Route path="/en/services/co2-calculator" element={<CO2Calculator />} />
        
        <Route path="/tr/services/co2-certificate" element={<CO2Certificate />} />
        <Route path="/de/services/co2-certificate" element={<CO2Certificate />} />
        <Route path="/en/services/co2-certificate" element={<CO2Certificate />} />
        
        <Route path="/tr/services/co2-exchange" element={<CO2Exchange />} />
        <Route path="/de/services/co2-exchange" element={<CO2Exchange />} />
        <Route path="/en/services/co2-exchange" element={<CO2Exchange />} />
        
        <Route path="/tr/add-co2-badge" element={<AddCO2Badge />} />
        <Route path="/de/add-co2-badge" element={<AddCO2Badge />} />
        <Route path="/en/add-co2-badge" element={<AddCO2Badge />} />
        
        <Route path="/tr/certificate-verification" element={<CertificateVerificationPage />} />
        <Route path="/de/certificate-verification" element={<CertificateVerificationPage />} />
        <Route path="/en/certificate-verification" element={<CertificateVerificationPage />} />
        
        {/* Certificate page - works with same URL regardless of language */}
        <Route path="/certificate/:certificateId" element={<CertificatePage />} />
        
        <Route path="/tr/contact" element={<ContactPage />} />
        <Route path="/de/contact" element={<ContactPage />} />
        <Route path="/en/contact" element={<ContactPage />} />
        
        {/* Checkout routes */}
        <Route path="/tr/checkout" element={<CheckoutPage />} />
        <Route path="/de/checkout" element={<CheckoutPage />} />
        <Route path="/en/checkout" element={<CheckoutPage />} />
        
        {/* German Legal Pages */}
        <Route path="/de/datenschutzerklaerung" element={<Datenschutzerklaerung />} />
        <Route path="/de/impressum" element={<Impressum />} />
        <Route path="/de/widerrufsbelehrung" element={<Widerrufsbelehrung />} />
        <Route path="/de/agb" element={<AGB />} />
        
        {/* Admin Panel Routes */}
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/tr/admin" element={<AdminPanel />} />
        <Route path="/de/admin" element={<AdminPanel />} />
        <Route path="/en/admin" element={<AdminPanel />} />
        <Route path="/ufukerma123456789" element={<AdminPanel />} />
        
        {/* Legacy routes - redirect to current language or browser language */}
        <Route path="/" element={<Navigate to={`/${getBestLanguage()}`} replace />} />
        <Route path="/services/co2-neutral-web" element={<Navigate to={`/${currentLanguage}/services/co2-neutral-web`} replace />} />
        <Route path="/services/co2-calculator" element={<Navigate to={`/${currentLanguage}/services/co2-calculator`} replace />} />
        <Route path="/services/co2-certificate" element={<Navigate to={`/${currentLanguage}/services/co2-certificate`} replace />} />
        <Route path="/services/co2-exchange" element={<Navigate to={`/${currentLanguage}/services/co2-exchange`} replace />} />
        <Route path="/add-co2-badge" element={<Navigate to={`/${currentLanguage}/add-co2-badge`} replace />} />
        <Route path="/certificate-verification" element={<Navigate to={`/${currentLanguage}/certificate-verification`} replace />} />
        <Route path="/contact" element={<Navigate to={`/${currentLanguage}/contact`} replace />} />
        <Route path="/admin" element={<Navigate to="/admin" replace />} />
      </Routes>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <LiveChatWidget />}
      {!isAdminRoute && <AccessibilityButton />}
    </div>
  );
};

// Home page component
const HomePage = () => (
  <>
    <Hero />
    <TreeCalculator />
    <VisitorBoost />
    <BigBrandsZeroCarbon />
    <CasinoTaxAdvantage />
    <CasinoCarbonAdvantage />
    <About />
    <Services />
    <CertificateVerification />
    <WhyUs />
    <VideoSection />
    <CertificateForm />
  </>
);

// Component to handle meta tag updates
const MetaTagUpdater = () => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Determine current page based on pathname
    let page = 'home';
    const path = location.pathname;
    
    if (path.includes('/contact')) {
      page = 'contact';
    } else if (path.includes('/certificate-verification')) {
      page = 'certificate';
    } else if (path.includes('/add-co2-badge')) {
      page = 'badge';
    } else if (path.includes('/services/')) {
      page = 'services';
    }
    
    // Update meta tags
    updateMetaTags(currentLanguage, page);
    
    // Force social media platforms to refresh cache
    const timestamp = Date.now();
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.content = `https://co2neutralpage.com/og-image.jpg?v=${timestamp}`;
    }
    
    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) {
      twitterImage.content = `https://co2neutralpage.com/og-image.jpg?v=${timestamp}`;
    }
    
    // Add additional meta tags for better social media support
    const addSocialMetaTags = () => {
      // Remove existing language-specific meta tags
      const existingTags = document.querySelectorAll('meta[data-dynamic="true"]');
      existingTags.forEach(tag => tag.remove());
      
      // Add new meta tags with data-dynamic attribute
      const tags = [
        { property: 'og:title', content: metaTags[currentLanguage]?.[page]?.ogTitle || metaTags.tr.home.ogTitle },
        { property: 'og:description', content: metaTags[currentLanguage]?.[page]?.ogDescription || metaTags.tr.home.ogDescription },
        { property: 'og:url', content: metaTags[currentLanguage]?.[page]?.ogUrl || metaTags.tr.home.ogUrl },
        { property: 'og:locale', content: metaTags[currentLanguage]?.[page]?.ogLocale || metaTags.tr.home.ogLocale },
        { property: 'twitter:title', content: metaTags[currentLanguage]?.[page]?.twitterTitle || metaTags.tr.home.twitterTitle },
        { property: 'twitter:description', content: metaTags[currentLanguage]?.[page]?.twitterDescription || metaTags.tr.home.twitterDescription },
        { property: 'twitter:url', content: metaTags[currentLanguage]?.[page]?.ogUrl || metaTags.tr.home.ogUrl },
        { name: 'description', content: metaTags[currentLanguage]?.[page]?.description || metaTags.tr.home.description },
        { name: 'keywords', content: metaTags[currentLanguage]?.[page]?.keywords || metaTags.tr.home.keywords }
      ];
      
      tags.forEach(tag => {
        const meta = document.createElement('meta');
        if (tag.property) {
          meta.setAttribute('property', tag.property);
        } else {
          meta.setAttribute('name', tag.name);
        }
        meta.setAttribute('content', tag.content);
        meta.setAttribute('data-dynamic', 'true');
        document.head.appendChild(meta);
      });
    };
    
    // Delay the meta tag update to ensure proper rendering
    setTimeout(addSocialMetaTags, 100);
    
  }, [currentLanguage, location.pathname]);

  return null;
};

function App() {
  return (
    <LanguageProvider>
      <Router>
        <MetaTagUpdater />
        <LanguageAwareApp />
      </Router>
    </LanguageProvider>
  );
}

export default App; 